import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { css } from "@repo/styled-system/css";

/**
 * The page shell every feature screen renders into. It owns route-change focus management (RGAA): on
 * mount / title change the `<h1>` receives focus, so keyboard + screen-reader users land on the new
 * page's heading instead of being stranded at the top of the chrome.
 */
export function PageScaffold({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  // `title` isn't read in the effect body; it's kept as a trigger so the heading is refocused on
  // every route/title change (RGAA), not just on mount.
  useEffect(() => {
    headingRef.current?.focus();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies
  }, [title]);

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "6",
        p: { base: "4", md: "6" },
        maxW: "5xl",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "3",
        })}
      >
        <h1
          ref={headingRef}
          tabIndex={-1}
          className={css({ fontSize: "2xl", fontWeight: "bold", outline: "none" })}
        >
          {title}
        </h1>
        {actions}
      </div>
      {children}
    </div>
  );
}
